/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type CSSProperties, type ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
// Import as a Vite asset (like the footer background) so the URL resolves in dev AND the MRT build.
// A raw `/images/...` path works in the dev server but 404s on Managed Runtime.
import highlightTexture from '/images/img-texture-highlight.webp';

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
@Component('footerTagline', {
    name: 'Footer Tagline',
    description: 'Large brand tagline with a per-word highlight band (ticker-tape) behind each word.',
    group: '4_BlackMagic_Footer',
})
@RegionDefinition([])
export class FooterTaglineMetadata {
    @AttributeDefinition({
        id: 'primaryText',
        name: 'Text',
        type: 'string',
        required: true,
        defaultValue: 'Build Living Soil',
    })
    primaryText?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Highlight Color',
        description: 'Brand highlight band shown behind each word. "none" leaves the words on the bare background.',
        type: 'enum',
        values: ['blue', 'green', 'orange', 'none'],
        defaultValue: 'blue',
    })
    backgroundColor?: string;
}
/* v8 ignore stop */

/** Highlight-color option → the design-system highlight class (tokens/highlight.css). */
const HIGHLIGHT_CLASS: Record<string, string> = {
    blue: 'highlight-blue',
    green: 'highlight-green',
    orange: 'highlight-orange',
};

export interface FooterTaglineProps {
    primaryText?: string;
    /** Brand highlight band behind each word (`blue` | `green` | `orange` | `none`). Defaults to `blue`. */
    backgroundColor?: string;
}

export default function FooterTagline({
    primaryText,
    backgroundColor = 'blue',
}: FooterTaglineProps): ReactElement | null {
    if (!primaryText?.trim()) return null;
    // One word per line, each on a ticker-tape band, the block tilted as a whole. The type
    // treatment, tilt, per-line stagger and band padding all live in theme/footer-tagline.css —
    // it's a one-off that matched no scale role, so as utility classes it was a wall of arbitrary
    // values. The merchandiser-picked `.highlight-*` class still supplies the band color +
    // organic-white text (tokens/highlight.css); `backgroundColor: 'none'` keeps bare words.
    // A tagline can legitimately repeat a word ("Grow Grow Grow"), so the word alone is not a unique
    // React key. Suffix each with how many times it has appeared so far — "the 2nd Grow" is a stable
    // identity for this list, and unlike the array index it survives the words being reordered.
    const seen = new Map<string, number>();
    const lines = primaryText
        .trim()
        .split(/\s+/)
        .map((word) => {
            const occurrence = (seen.get(word) ?? 0) + 1;
            seen.set(word, occurrence);
            return { word, key: `${word}-${occurrence}` };
        });
    const highlight = HIGHLIGHT_CLASS[backgroundColor];
    return (
        // The texture URL is the one thing that can't live in the stylesheet: it has to come from a
        // Vite asset import so the build emits a bundle-correct path (a literal `/images/…` in CSS
        // 404s on Managed Runtime). Declared once here and inherited by every band.
        <p
            className="footer-tagline"
            style={{ '--highlight-texture-image': `url(${highlightTexture})` } as CSSProperties}>
            {lines.map(({ word, key }) => (
                <span key={key} className="footer-tagline__line">
                    {/* `highlight-texture` rides along with `highlight` so a "none" color leaves
                        bare words with no overlay to blend against. */}
                    <span className={cn('footer-tagline__band', highlight && cn(highlight, 'highlight-texture'))}>
                        {word}
                    </span>
                </span>
            ))}
        </p>
    );
}
